import type { ComponentProps } from 'react'
import React, { useCallback, useMemo } from 'react'
import styled from 'styled-components'
import ModalBase from 'LuxKit/Modal/ModalBase'
import type { EnabledFilters } from 'components/Search/type'
import HotelSearchPageFilterPane, {
} from 'components/Pages/HotelSearchPage/HotelSearchPageFilterPane'
import TextButton from 'LuxKit/Button/TextButton'
import ModalBody from 'LuxKit/Modal/ModalBody'
import ModalFooter from 'LuxKit/Modal/ModalFooter'
import type { OfferListSortOption } from 'constants/offerListFilters'
import { queryKeyFilters } from 'constants/url'
import { deleteSearchParams } from 'lib/url/searchUrlUtils'
import { replace } from 'connected-react-router'
import { useAppDispatch } from 'hooks/reduxHooks'
import { connect } from 'react-redux'
import { getAppliedFilterCount } from 'components/Search/utils'
import Group from 'components/utils/Group'
import ModalContent from 'LuxKit/Modal/ModalContent'
import { STORE_MAP_PAGE_ZOOM } from 'lib/search/constants'
import CSSBreakpoint from 'components/utils/CSSBreakpoint'

const ZoomedContent = styled.div<{ $applyZoom?: boolean }>`
  ${props => props.$applyZoom && `
    zoom: ${STORE_MAP_PAGE_ZOOM};
  `}
`

interface MappedStateProps {
  currentHash: string
}

type InheritedHotelSearchPageFilterPaneProps = Pick<ComponentProps<typeof HotelSearchPageFilterPane>, 'onFilterChange'>

interface Props extends InheritedHotelSearchPageFilterPaneProps {
  filters: App.OfferListFilters
  search: string
  region?: string
  enabledFilters: EnabledFilters
  open?: boolean
  onClose?: () => void
  fetching?: boolean
  sortOptions?: Array<OfferListSortOption>
  hideCompareOffersToggle?: boolean
  hideAddDestinationToggle?: boolean
  excludeOfferTypesFromCount?: boolean
  applyZoom?: boolean
}

function OfferListFilterSidebar({
  currentHash,
  open,
  onClose,
  fetching,
  sortOptions,
  filters,
  search,
  enabledFilters,
  onFilterChange,
  hideCompareOffersToggle = false,
  hideAddDestinationToggle = false,
  excludeOfferTypesFromCount = false,
  applyZoom = false,
}: Props & MappedStateProps) {
  const dispatch = useAppDispatch()

  const resetBtnText = useMemo(() => {
    const filterCount = getAppliedFilterCount(filters, enabledFilters, excludeOfferTypesFromCount)
    return filterCount > 0 ? `Reset all (${filterCount})` : 'Reset all'
  }, [filters, enabledFilters, excludeOfferTypesFromCount])

  const onReset = useCallback(() => {
    const filtersToDelete = [...queryKeyFilters]
    const newSearch = deleteSearchParams(
      search,
      ...filtersToDelete,
    )
    dispatch(replace({ search: newSearch, hash: currentHash }))
  }, [currentHash, dispatch, search])

  return (
    <ModalBase
      mode="drawer"
      isOpen={open}
      onClose={onClose}
    >
      <ModalBody>
        <ModalContent>
          <ZoomedContent $applyZoom={applyZoom}>
            <HotelSearchPageFilterPane
              fetching={fetching}
              filters={filters}
              mapViewURL=""
              search={search}
              enabledFilters={enabledFilters}
              sortOptions={sortOptions}
              onClose={onClose}
              onFilterChange={onFilterChange}
              hideCompareOffersToggle={hideCompareOffersToggle}
              hideAddDestinationToggle={hideAddDestinationToggle}
              applyZoomCompensation={applyZoom}
            />
          </ZoomedContent>
        </ModalContent>
      </ModalBody>
      <ModalFooter>
        <ZoomedContent $applyZoom={applyZoom}>
          <Group direction="horizontal" horizontalAlign="stretch">
            <CSSBreakpoint max="mobile">
              <TextButton kind="tertiary" size="large" onClick={onReset}>
                {resetBtnText}
              </TextButton>
            </CSSBreakpoint>
            <TextButton kind="primary" size="large" onClick={onClose}>
              View Offers
            </TextButton>
          </Group>
        </ZoomedContent>
      </ModalFooter>
    </ModalBase>
  )
}

function mapStateToProps(state: App.State): MappedStateProps {
  return {
    currentHash: state.router.location.hash,
  }
}

export default connect(mapStateToProps)(OfferListFilterSidebar)
